/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React context
import { useMaterialUIController, setLayout } from "context";
import { useBranding, FRESENIUS } from "contextBranding";

const SBI_DASHBOARD_ROUTES = ["/sbidark", "/e-learning", "/connect", "/digital-assessment"];
const SBI_ROUTES_PREFIX = "/e-learning/course/";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sbiDashboardTheme } = controller;
  const { pathname } = useLocation();
  const { isFreseniusBranding } = useBranding();
  const isSbiRoute =
    SBI_DASHBOARD_ROUTES.includes(pathname) ||
    pathname.startsWith(SBI_ROUTES_PREFIX);
  const noSidebar = isSbiRoute;
  const isSbiDashboard = pathname === "/sbidark";
  const useSbiTheme =
    isSbiRoute &&
    (pathname === "/sbidark" ||
      pathname === "/e-learning" ||
      pathname.startsWith(SBI_ROUTES_PREFIX) ||
      pathname === "/connect" ||
      pathname === "/digital-assessment");

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [pathname]);

  return (
    <MDBox
      sx={({ breakpoints, transitions, functions: { pxToRem }, palette }) => ({
        py: 1,
        px: 2,
        position: "relative",
        ...(noSidebar
          ? {
              marginLeft: 0,
              padding: 0,
              minHeight: "100vh",
              ...(useSbiTheme
                ? {
                    backgroundColor: isFreseniusBranding
                      ? (sbiDashboardTheme === "light"
                          ? FRESENIUS.themeLight.bgMain
                          : FRESENIUS.themeDark.bgMain)
                      : sbiDashboardTheme === "light"
                        ? "#ffffff"
                        : "#1a1f23",
                  }
                : {
                    backgroundColor:
                      palette?.surfaceSbi?.card ?? palette?.background?.default,
                    }),
            }
          : {
              [breakpoints.up("xl")]: {
                marginLeft: miniSidenav ? pxToRem(104) : pxToRem(228),
                transition: transitions.create(
                  ["margin-left", "margin-right"],
                  {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.standard,
                  },
                ),
              },
            }),
      })}
    >
      {children}
    </MDBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
